import React from 'react';
// import { Button, Grid, makeStyles } from '@material-ui/core';
import { Grid, makeStyles } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import { CustomEntityAboutCard } from './CustomEntityAboutCard';
import {
  EntityApiDefinitionCard,
  // EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  // EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  // EntityDependsOnComponentsCard,
  // EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  // EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  // hasRelationWarnings,
  // EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
// import { EmptyState } from '@backstage/core-components';
// import {
//   Direction,
//   EntityCatalogGraphCard,
// } from '@backstage/plugin-catalog-graph';
// import {
//   RELATION_API_CONSUMED_BY,
//   RELATION_API_PROVIDED_BY,
//   RELATION_CONSUMES_API,
//   RELATION_DEPENDENCY_OF,
//   RELATION_DEPENDS_ON,
//   RELATION_HAS_PART,
//   RELATION_PART_OF,
//   RELATION_PROVIDES_API,
// } from '@backstage/catalog-model';

import { useEntity, useEntityOwnership } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';

// Kuberenetes
import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';

// Harbor
import {
  HarborPage,
  HarborWidget,
  isHarborAvailable,
} from '@digitalist-open-cloud/backstage-plugin-harbor'

// K8s Exec
import {
  K8SExecPage,
  isK8sExecEnabled
} from '@internal/backstage-plugin-k8s-exec';

// Grafana (k-phoen plugin)
import {
  EntityGrafanaDashboardsCard,
  EntityGrafanaAlertsCard,
  isDashboardSelectorAvailable,
  isAlertSelectorAvailable,
  isOverviewDashboardAvailable,
  overviewDashboardFromEntity,
} from '@k-phoen/backstage-plugin-grafana';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { grafanaEntityReadPermission } from '../../permissions/grafanaPermissions';
import { useIdentityRoles } from '../../utils/useIdentityRoles';

/**
 * Renders the entity's overview dashboard in kiosk mode (Grafana chrome hidden)
 * inside a fixed-height container so the iframe fills the available area.
 *
 * Kiosk mode: appending `&kiosk` to the URL removes the top nav bar, side
 * menu, and panel controls from Grafana, leaving only the dashboard panels.
 * The height is set to 85vh so it fills the viewport without needing to know
 * the exact dashboard panel count in advance.
 *
 * HOW THE EMBED AUTHENTICATES — see backstage/docs/GRAFANA-EMBED-AUTH-NOTES.md
 * before changing anything here. Short version:
 *
 *  - `grafana.embedDomain` is a SECOND hostname on the SAME Grafana. A Gateway
 *    API filter on that hostname injects `X-WEBAUTH-USER: backstage-embed`, so
 *    Grafana's auth-proxy logs every viewer in as one fixed service identity.
 *    Viewers need no Grafana account. Do not point this at `grafana.domain` —
 *    that host strips the header and would demand a per-user Grafana login.
 *  - That identity (`backstage-embed`, a Grafana user, Viewer) gets its
 *    dashboard access from Grafana TEAM membership (`teachers`,
 *    `eecs-research`). It is unrelated to the `glsa_` token in
 *    GRAFANA_TOKEN, which is a separate service account used only for
 *    backend API calls (the Dashboards and Alerts cards below).
 *  - Grafana is QMUL-internal (138.37.17.189); Backstage is public. So this
 *    iframe only loads for viewers on the QMUL network / Fortinet VPN, while
 *    the Dashboards and Alerts cards work anywhere because the backend
 *    fetches those. A blank panel beside populated cards is that, not a bug.
 */
const GrafanaKioskViewer: React.FC = () => {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const rawUrl = overviewDashboardFromEntity(entity);
  const grafanaDomain = configApi.getString('grafana.domain');
  const embedDomain = configApi.getOptionalString('grafana.embedDomain') ?? grafanaDomain;
  const embedUrl = rawUrl.replace(grafanaDomain, embedDomain);
  const kioskUrl = embedUrl.includes('?') ? `${embedUrl}&kiosk` : `${embedUrl}?kiosk`;
  return (
    <div style={{ width: '100%', height: '85vh' }}>
      <iframe
        src={kioskUrl}
        style={{ width: '100%', height: '100%', border: 0 }}
        title="Grafana dashboard"
      />
    </div>
  );
};

// DevContainers
// import {
//   type DevcontainersConfig,
//   DevcontainersProvider,
//   ExampleDevcontainersComponent,
// } from '@coder/backstage-plugin-devcontainers-react';

// The value of tagName must match the tag value that
// backstage-plugin-devcontainers-backend is configured with
// const devcontainersConfig: DevcontainersConfig = {
//   tagName: 'devcontainers',
// };

const techdocsContent = (
  <EntityTechdocsContent>
  </EntityTechdocsContent>
);

// Wrapper to make child cards fill the Grid cell height
const useFullHeightStyles = makeStyles(() => ({
  root: {
    height: '100%',
    '& .MuiCard-root': {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
  },
}));

const FullHeightWrapper: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const classes = useFullHeightStyles();
  return <div className={classes.root}>{children}</div>;
};

const useClickableHarborStyles = makeStyles(theme => ({
  clickableCard: {
    cursor: 'pointer',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 3,
    },
  },
}));

/**
 * Harbor tab content with two columns hidden.
 *
 * The upstream plugin (@digitalist-open-cloud/backstage-plugin-harbor 0.4.5)
 * exports only <HarborPage /> with no props for disabling columns, so we hide
 * them with scoped CSS. Its column order is fixed as:
 *
 *   1 Tag title | 2 Size MB | 3 Vulnerabilities severity
 *   4 Total Vulnerabilities | 5 Push Time | 6 Pull Time | 7 Learn More
 *
 * Column 7 ("Learn More") deep-links into the Harbor UI, which is not exposed
 * to end users, so it always times out. Column 4 is a raw CVE count that is
 * dominated by base-image findings the user cannot act on, so it alarms more
 * than it informs. Column 3 (severity band) is deliberately KEPT so we stay
 * transparent about the fact that scanning happens.
 *
 * NOTE: index-based, so re-check these positions if the plugin is upgraded.
 */
const useHarborColumnHidingStyles = makeStyles({
  root: {
    '& table th:nth-child(4), & table td:nth-child(4)': { display: 'none' },
    '& table th:nth-child(7), & table td:nth-child(7)': { display: 'none' },
  },
});

const HarborTabContent: React.FC = () => {
  const classes = useHarborColumnHidingStyles();
  return (
    <div className={classes.root}>
      <HarborPage />
    </div>
  );
};

const HarborWidgetRedirectWrapper: React.FC = () => {
  const { entity } = useEntity();
  const navigate = useNavigate();
  const classes = useClickableHarborStyles();

  const harborRoute = React.useMemo(() => {
    if (!entity?.metadata?.name) {
      return undefined;
    }

    const kind = (entity.kind ?? 'component').toLocaleLowerCase('en-US');
    const namespace = (entity.metadata.namespace ?? 'default').toLocaleLowerCase('en-US');
    const name = entity.metadata.name.toLocaleLowerCase('en-US');

    const encodedNamespace = encodeURIComponent(namespace);
    const encodedKind = encodeURIComponent(kind);
    const encodedName = encodeURIComponent(name);

    return `/catalog/${encodedNamespace}/${encodedKind}/${encodedName}/harbor`;
  }, [entity]);

  const handleNavigate = React.useCallback(() => {
    if (!harborRoute) {
      return;
    }
    navigate(harborRoute);
  }, [harborRoute, navigate]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleNavigate();
      }
    },
    [handleNavigate],
  );

  return (
    <div
      role="link"
      tabIndex={harborRoute ? 0 : -1}
      aria-disabled={!harborRoute}
      aria-label="View Harbor details"
      className={classes.clickableCard}
      onClick={handleNavigate}
      onKeyDown={handleKeyDown}
    >
      <HarborWidget />
    </div>
  );
};

// const cicdContent = (
//   // This is an example of how you can implement your company's logic in entity page.
//   // You can for example enforce that all components of type 'service' should use GitHubActions
//   <EntitySwitch>
//     {/*
//       Here you can add support for different CI/CD services, for example
//       using @backstage-community/plugin-github-actions as follows:
//       <EntitySwitch.Case if={isGithubActionsAvailable}>
//         <EntityGithubActionsContent />
//       </EntitySwitch.Case>
//      */}
//
//     <EntitySwitch.Case>
//       <EmptyState
//         title="No CI/CD available for this entity"
//         missing="info"
//         description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
//         action={
//           <Button
//             variant="contained"
//             color="primary"
//             href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
//           >
//             Read more
//           </Button>
//         }
//       />
//     </EntitySwitch.Case>
//   </EntitySwitch>
// );

// Monitoring (Grafana) tab content.
//
// We render three pieces that Roadie offers, each one self-guards with an
// `if` predicate matching annotations, so an entity that only declares a
// subset (e.g. dashboards but no alerts) still renders cleanly.
//
// The outermost <RequirePermission> is a defense-in-depth check against the
// backend policy (grafana.entity.read). UI tab visibility is also gated by
// `useIdentityRoles` on the caller side; a student who bypasses the UI and
// hits this route directly will get the permission error page instead.
// EntitySwitch.Case `if` expects (entity, context) => boolean, but the
// @k-phoen plugin predicates return string | undefined. This helper
// adapts any annotation-check function into the required signature.
const asEntityPredicate =
  (fn: (entity: any) => any) =>
  (entity: any, _context: any): boolean =>
    Boolean(fn(entity));

const monitoringContent = (
  <RequirePermission permission={grafanaEntityReadPermission}>
    <Grid container spacing={3} alignItems="stretch">
      <EntitySwitch>
        <EntitySwitch.Case if={asEntityPredicate(isOverviewDashboardAvailable)}>
          <Grid item xs={12}>
            <GrafanaKioskViewer />
          </Grid>
        </EntitySwitch.Case>
      </EntitySwitch>

      <EntitySwitch>
        <EntitySwitch.Case if={asEntityPredicate(isDashboardSelectorAvailable)}>
          <Grid item md={12} xs={12}>
            <EntityGrafanaDashboardsCard />
          </Grid>
        </EntitySwitch.Case>
      </EntitySwitch>

      <EntitySwitch>
        <EntitySwitch.Case if={asEntityPredicate(isAlertSelectorAvailable)}>
          <Grid item md={12} xs={12}>
            <EntityGrafanaAlertsCard />
          </Grid>
        </EntitySwitch.Case>
      </EntitySwitch>
    </Grid>
  </RequirePermission>
);

/**
 * Predicate for the Monitoring tab's `if` prop: show only when the entity
 * carries at least one Grafana annotation. This keeps the tab list tidy on
 * entities that don't opt in.
 */
const hasGrafanaAnnotations = (entity: any): boolean => {
  const a = entity?.metadata?.annotations ?? {};
  return Boolean(
    a['grafana/dashboard-selector'] ||
      a['grafana/alert-label-selector'] ||
      a['grafana/overview-dashboard'],
  );
};

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    
    {/* disable hasrelationwarnings and its imports */}
    {/* <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch> */}

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={4} xs={12}>
      <CustomEntityAboutCard variant="gridItem" />
    </Grid>
    {/* <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid> */}

    <Grid item md={4} xs={12}>
      <FullHeightWrapper>
        <EntityLinksCard variant="gridItem" />
      </FullHeightWrapper>
    </Grid>
    {/* <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid> */}

    <EntitySwitch>
      <EntitySwitch.Case if={isHarborAvailable}>
        <Grid item md={4} xs={12}>
          <FullHeightWrapper>
            <HarborWidgetRedirectWrapper />
          </FullHeightWrapper>
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  
    {/* <Grid item md={6} xs={12}>
      <DevcontainersProvider config={devcontainersConfig}>
        <ExampleDevcontainersComponent />
      </DevcontainersProvider>
    </Grid> */}
  </Grid>
);

// Hide or disable unregister for non-admins; avoid useEntity outside of EntityLayout
const OwnerAwareEntityLayout: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  useEntityOwnership();
  // Driven by the shared `useIdentityRoles` hook (reads
  // `permission.rbac.privilegedGroups` via the same RoleResolver as the
  // backend) so this layout doesn't duplicate the identity-fetch /
  // role-resolve `useEffect` that pages like ServiceEntityPage /
  // DashboardEntityPage already perform via the same hook.
  const { isAdmin } = useIdentityRoles();

  // Without using useEntity outside of EntityLayout, only enable unregister for admins
  const canUnregister = isAdmin;

  return (
    <EntityLayout UNSTABLE_contextMenuOptions={{ disableUnregister: canUnregister ? 'visible' : 'hidden' }}>
      {children}
    </EntityLayout>
  );
};

// Styles to hide edit/refresh actions within the Group profile card header
const useGroupStyles = makeStyles(() => ({
  hideGroupActions: {
    // Hide Edit
    '& [title="Edit Metadata"], & [aria-label="Edit"]': {
      display: 'none !important',
    },
    // Hide Refresh (note the title differs on group card)
    '& [title="Schedule entity refresh"], & [aria-label="Refresh"]': {
      display: 'none !important',
    },
  },
}));

const GroupProfileSection: React.FC = () => {
  const classes = useGroupStyles();
  // For group pages, only admins should see the card-header Edit /
  // Refresh actions. Driven by the shared `useIdentityRoles` hook
  // (reads `permission.rbac.privilegedGroups` via the same RoleResolver
  // as the backend) — previously this component hardcoded
  // `'group:default/backstage-admins'` / `'group:default/teachers'`
  // string membership checks, which silently became wrong when PR A/C
  // moved RBAC inputs to config.
  const { isAdmin } = useIdentityRoles();

  return (
    <div className={!isAdmin ? classes.hideGroupActions : undefined}>
      <EntityGroupProfileCard variant="gridItem" />
    </div>
  );
};

// Styles to hide ALL card header actions for non-admins on Group pages
// (Removed global wrapper to keep code simple; using targeted GroupProfileSection styling instead)

/**
 * Service entity page.
 *
 * Rendered as a component (not a const JSX tree) so we can consult the
 * current user's identity at render time and conditionally include the
 * Monitoring tab only for staff (admin / umbrella reader / scope-wide
 * reader). Plain students never see the tab; if they try to navigate to
 * /monitoring directly, the <RequirePermission> guard inside
 * `monitoringContent` will render the standard permission-denied page.
 *
 * Researcher access is mediated by the catalog read policy, not by this
 * gate: a researcher cannot reach an ECS639U/ECS519U entity in the
 * first place because the catalog API filters those out. So switching
 * this gate to `isStaff` is strictly a permissive UI tweak that lets
 * researchers see the Monitoring tab on the entities they CAN read
 * (e.g. dashboards under spec.system: eecs-research).
 */
const ServiceEntityPage: React.FC = () => {
  const { isStaff } = useIdentityRoles();

  return (
    <OwnerAwareEntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        if={isK8sExecEnabled}
        path="/k8s-exec"
        title="Terminal"
      >
        <K8SExecPage />
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/kubernetes"
        title="Kubernetes"
        if={isKubernetesAvailable}
      >
        <EntityKubernetesContent />
      </EntityLayout.Route>

      {/* Monitoring tab: staff (admin / umbrella / scope-wide reader)
          only, and only when the entity declares at least one grafana/*
          annotation. */}
      {isStaff && (
        <EntityLayout.Route
          path="/monitoring"
          title="Monitoring"
          if={hasGrafanaAnnotations}
        >
          {monitoringContent}
        </EntityLayout.Route>
      )}

      <EntityLayout.Route path="/docs" title="Docs">
        {techdocsContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/harbor" title="Harbor" if={isHarborAvailable}>
        <HarborTabContent />
      </EntityLayout.Route>
    </OwnerAwareEntityLayout>
  );
};

const serviceEntityPage = <ServiceEntityPage />;

/**
 * Dashboard entity page (`spec.type: dashboard`).
 *
 * Used for catalog entities whose primary content IS an embedded
 * Grafana dashboard (e.g. the GPU capacity dashboard for EECS Research,
 * future research / teaching cohort observability surfaces). Tab named
 * "Dashboard" rather than "Monitoring" since for these entities it
 * isn't auxiliary monitoring — it's the entity's whole purpose. Gated
 * by `isStaff` (admin / umbrella reader / scope-wide reader) to match
 * the backend grafanaEntityReadPermission policy from PR A.
 *
 * Skipping Kubernetes / K8s-Exec / Harbor tabs because dashboard
 * components don't deploy anything — those tabs would be dead UI.
 */
const DashboardEntityPage: React.FC = () => {
  const { isStaff } = useIdentityRoles();

  return (
    <OwnerAwareEntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      {isStaff && (
        <EntityLayout.Route
          path="/monitoring"
          title="Dashboard"
          if={hasGrafanaAnnotations}
        >
          {monitoringContent}
        </EntityLayout.Route>
      )}

      <EntityLayout.Route path="/docs" title="Docs">
        {techdocsContent}
      </EntityLayout.Route>
    </OwnerAwareEntityLayout>
  );
};

const dashboardEntityPage = <DashboardEntityPage />;

const websiteEntityPage = (
  <OwnerAwareEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    {/* <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route> */}

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    {/* <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route> */}

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </OwnerAwareEntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <OwnerAwareEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </OwnerAwareEntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('dashboard')}>
      {dashboardEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <OwnerAwareEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <CustomEntityAboutCard />
        </Grid>
        {/* <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid> */}
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </OwnerAwareEntityLayout>
);

const userPage = (
  <OwnerAwareEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </OwnerAwareEntityLayout>
);

const groupPage = (
  <OwnerAwareEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <GroupProfileSection />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </OwnerAwareEntityLayout>
);

const systemPage = (
  <OwnerAwareEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <CustomEntityAboutCard variant="gridItem" />
        </Grid>
        {/* <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid> */}
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route> */}
  </OwnerAwareEntityLayout>
);

const domainPage = (
  <OwnerAwareEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <CustomEntityAboutCard variant="gridItem" />
        </Grid>
        {/* <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid> */}
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </OwnerAwareEntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
